/**
 * Orbit camera for the terrain viewport.
 *
 * Deliberately hand-written rather than pulled from a library: the renderer
 * needs nothing but a view ray per pixel, so a full scene-graph camera would be
 * dead weight. The state is the three numbers an orbit control actually has —
 * an azimuth, an elevation and a distance about a target point.
 *
 * Conventions: world space is a unit square in XZ, `y` is up, and the terrain
 * occupies `y ∈ [0, 1]` after normalisation. Angles are radians.
 */

export interface CameraState {
  /** Rotation about the world Y axis. */
  azimuth: number;
  /** Elevation above the horizon, clamped away from the poles. */
  elevation: number;
  /** Distance from the target. */
  distance: number;
  /** Look-at point. */
  target: [number, number, number];
}

const MIN_ELEVATION = 0.04;
const MAX_ELEVATION = Math.PI / 2 - 0.02;
const MIN_DISTANCE = 0.35;
const MAX_DISTANCE = 8;

/**
 * Initial camera for the unit cube the renderer works in.
 *
 * The world box is 1 × `heightRatio` × 1, so the orientation and the distance
 * are pure geometry: `frameTerrain` solves for a distance that fits the box in
 * both axes, and the elevation is steep enough that rays enter the box from
 * above rather than slipping underneath it.
 */
export function defaultCamera(heightRatio = 0.22, aspectRatio = 1.5): CameraState {
  return frameTerrain(
    { azimuth: -0.75, elevation: 0.72, distance: 1, target: [0.5, heightRatio / 2, 0.5] },
    heightRatio,
    aspectRatio,
  );
}

/**
 * Reframe for a given terrain aspect, keeping the current orientation.
 *
 * `heightRatio` is `maxHeight / worldExtent`, which is what decides how tall the
 * box looks: a 1400 m massif over a 4 km plain is a very different shape from a
 * 180 m dune field, and a fixed distance either crops the first or strands the
 * second. Solving for the distance that fits the box in both axes keeps the view
 * correct on every preset.
 */
export function frameTerrain(c: CameraState, heightRatio: number, aspectRatio: number): CameraState {
  // Fit the box's diagonal footprint horizontally and its full height
  // vertically, then take whichever constraint binds. The margin is generous
  // because a terrain that exactly fills the frame reads as a wall of rock
  // rather than as a landform — a little sky around it is what makes the relief
  // legible.
  const halfTan = Math.tan(FOV_Y / 2);
  const fitHorizontal = Math.SQRT2 / (2 * halfTan * Math.max(0.2, aspectRatio));
  const fitVertical = Math.max(heightRatio, 0.4) / (2 * halfTan);
  return clampCamera({
    ...c,
    distance: Math.max(fitHorizontal, fitVertical) * 1.55,
    target: [0.5, heightRatio * 0.5, 0.5],
  });
}

export function clampCamera(c: CameraState): CameraState {
  return {
    azimuth: c.azimuth,
    elevation: Math.min(MAX_ELEVATION, Math.max(MIN_ELEVATION, c.elevation)),
    distance: Math.min(MAX_DISTANCE, Math.max(MIN_DISTANCE, c.distance)),
    target: c.target,
  };
}

export interface CameraBasis {
  /** Eye position in world space. */
  eye: [number, number, number];
  /** Orthonormal camera basis: right, up, and the forward direction (into the screen). */
  right: [number, number, number];
  up: [number, number, number];
  forward: [number, number, number];
  /** tan(fov/2) used to scale NDC into the image plane. */
  halfTan: number;
  aspect: number;
}

export const FOV_Y = (42 * Math.PI) / 180;

/**
 * Build the orthonormal basis for a camera state.
 *
 * The ray for a pixel is then
 *     dir = normalize(forward + right * (ndcX * aspect * halfTan) + up * (ndcY * halfTan))
 * with `ndcX`, `ndcY` in [-1, 1].
 */
export function cameraBasis(c: CameraState, aspect: number): CameraBasis {
  const ce = Math.cos(c.elevation);
  const se = Math.sin(c.elevation);
  const ca = Math.cos(c.azimuth);
  const sa = Math.sin(c.azimuth);

  // Forward points from the eye towards the target.
  const forward: [number, number, number] = [-ce * ca, -se, -ce * sa];
  const eye: [number, number, number] = [
    c.target[0] - forward[0] * c.distance,
    c.target[1] - forward[1] * c.distance,
    c.target[2] - forward[2] * c.distance,
  ];
  // Right is forward x worldUp, normalised; up then closes the frame.
  const worldUp: [number, number, number] = [0, 1, 0];
  let right: [number, number, number] = [
    forward[1] * worldUp[2] - forward[2] * worldUp[1],
    forward[2] * worldUp[0] - forward[0] * worldUp[2],
    forward[0] * worldUp[1] - forward[1] * worldUp[0],
  ];
  const rl = Math.hypot(right[0], right[1], right[2]) || 1;
  right = [right[0] / rl, right[1] / rl, right[2] / rl];
  const up: [number, number, number] = [
    right[1] * forward[2] - right[2] * forward[1],
    right[2] * forward[0] - right[0] * forward[2],
    right[0] * forward[1] - right[1] * forward[0],
  ];

  return { eye, right, up, forward, halfTan: Math.tan(FOV_Y / 2), aspect };
}

/* -------------------------------------------------------------------------- */
/* Interaction                                                                */
/* -------------------------------------------------------------------------- */

export interface PointerSample {
  x: number;
  y: number;
}

/** Apply an orbit/pan/zoom gesture to a camera state, returning a new one. */
export function applyOrbit(
  c: CameraState,
  dx: number,
  dy: number,
  button: 'rotate' | 'pan',
  viewportHeight: number,
): CameraState {
  if (button === 'rotate') {
    // Sign convention: the horizon follows the pointer.
    //
    // The camera orbits the target, so rotating the camera one way sweeps the
    // scene the *other* way on screen. `azimuth: -dx` therefore moves the
    // terrain in the same direction as the drag, which reads as the ground being
    // dragged out from under the cursor; `+dx` reads as the camera being pushed,
    // and is the inverted feeling this fixes. The vertical axis already had the
    // right sense — dragging down raises the elevation, which swings the terrain
    // down the screen — so only the horizontal one changes.
    return clampCamera({
      ...c,
      azimuth: c.azimuth + dx * 0.006,
      elevation: c.elevation + dy * 0.006,
    });
  }

  // Pan translates the target in the camera's screen plane, scaled so the drag
  // stays anchored to the terrain at the target's depth. The world-units-per-pixel
  // figure comes from the vertical extent the frustum covers at that distance,
  // which is why the field of view — not the aspect ratio — is the right input.
  const worldPerPixel = (2 * c.distance * Math.tan(FOV_Y / 2)) / Math.max(1, viewportHeight);
  // The camera's own basis, so panning stays correct at any orientation. It is
  // built with a dummy aspect because only `right` and `up` are used, and neither
  // depends on it.
  const basis = cameraBasis(c, 1);
  // Dragging right should move the terrain right, i.e. move the target left.
  const tx = -dx * worldPerPixel;
  const ty = dy * worldPerPixel;
  return clampCamera({
    ...c,
    target: [
      c.target[0] + basis.right[0] * tx + basis.up[0] * ty,
      c.target[1] + basis.right[1] * tx + basis.up[1] * ty,
      c.target[2] + basis.right[2] * tx + basis.up[2] * ty,
    ],
  });
}

export function applyZoom(c: CameraState, deltaY: number): CameraState {
  return clampCamera({ ...c, distance: c.distance * Math.exp(deltaY * 0.0012) });
}
